import { useState } from 'react'
import { Play } from 'lucide-react'
import { startWalkthrough } from '@/features/companion/companion-walkthrough-store'
import { completionFor, resolveWalkthroughPanel } from '@/features/companion/tool-executor'
import type { CompanionConversation } from './use-companion-conversation'

/**
 * "Start walkthrough" under a reply that found a guided flow but did not start
 * it: the thread's pending slot holds the tour (spec 2026-10-18, 6.2). The user
 * adds or picks the panel, presses the button, and the tour starts anchored to
 * it. Pressing it with the panel still missing keeps the button and says so;
 * the assistant never opens panels on its own. Start and Dismiss withdraw the
 * offer from the thread, so a reopened one does not offer it again.
 */
export function PendingWalkthroughAction({
  conversation,
}: {
  conversation: CompanionConversation
}) {
  const { manager, pending } = conversation
  const [note, setNote] = useState<string | null>(null)

  const offer = pending?.kind === 'walkthrough_offer' ? pending.walkthrough : undefined
  if (!offer) return null

  const start = () => {
    const anchor = resolveWalkthroughPanel(offer.panel)
    if ('error' in anchor) {
      setNote(
        anchor.reason === 'ambiguous'
          ? `More than one ${anchor.label} is open. Click the one you want, then try again.`
          : `Still no ${offer.label} open.`,
      )
      return
    }
    setNote(null)
    manager.withdraw('walkthrough_offer')
    startWalkthrough(offer.steps, { panelId: anchor.panelId }, completionFor(offer.slug), {
      slug: offer.slug ?? null,
      source: 'chat',
    })
  }

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 pb-2" data-testid="pending-walkthrough">
      <button
        type="button"
        onClick={start}
        className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
      >
        <Play size={12} />
        Start walkthrough
      </button>
      <button
        type="button"
        onClick={() => {
          setNote(null)
          manager.withdraw('walkthrough_offer')
        }}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Dismiss
      </button>
      {note && <span className="text-xs text-muted-foreground">{note}</span>}
    </div>
  )
}
